import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useState, Fragment, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { Routes } from 'config';
import { useAppSelector } from 'store';
import { useIsDesktopLarge } from 'hooks';
import { useCourseContext } from 'context';
import { AccentMaskIcon } from 'components/icons';
import { selectIsGuestUser } from 'store/slices/auth';
import { BurgerButton } from 'components/burger-button';

import { Button } from '../button';

import { StepButton } from './step-button';
import { ResponsiveMenu } from './responsive-menu';
import { LanguageButton } from './language-button';
import { NavigationLink } from './navigation-link';
import { NavLinkItem, TopNavigationProps } from './types';

const links: NavLinkItem[] = [
  {
    i18nKey: 'dashboard',
    href: Routes.Dashboard.Home,
  },
  {
    i18nKey: 'resources',
    href: Routes.Resources.Home,
  },
  {
    i18nKey: 'contactUs',
    href: Routes.ContactUs.Home,
  },
];

const commonButtonStyle =
  'flex min-w-10 items-center justify-center rounded-full h-10 w-10';

export const TopNavigation = ({ logoUrl }: TopNavigationProps) => {
  const { internalSteps } = useCourseContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isGuestUser = useAppSelector(selectIsGuestUser);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isDesktopLarge = useIsDesktopLarge();
  const { t } = useTranslation(['header']);

  useEffect(() => {
    if (isDesktopLarge) {
      setIsMobileMenuOpen(false);
    }
  }, [isDesktopLarge]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const logo = logoUrl && (
    <div className="h-6 max-md:w-6 max-md:overflow-hidden md:h-9">
      <Link to={Routes.Dashboard.Home}>
        <img
          alt="logo"
          src={logoUrl}
          className="h-full w-auto max-md:max-w-[unset] md:object-contain"
        />
      </Link>
    </div>
  );

  return (
    <>
      <div className="relative z-5 flex h-12 w-full items-center md:h-16 md:items-center md:justify-between">
        <div className="flex shrink-0 items-center">{logo}</div>
        <div className="absolute left-1/2 hidden flex-1 -translate-x-1/2 pl-8 xl:flex">
          {links.map((link) => (
            <NavigationLink
              key={link.href}
              href={link.href}
              variant="desktop"
              label={t(link.i18nKey)}
              onClick={handleLinkClick}
              isActive={pathname === link.href}
            />
          ))}
        </div>

        <div className="flex items-center max-md:flex-1">
          <div className="flex flex-1 items-center justify-center">
            {internalSteps?.map((item, index) => (
              <Fragment key={item.presentStep.id}>
                {index !== 0 && (
                  <div className="bg-gray-divider h-px min-w-2 md:min-w-4" />
                )}
                <StepButton
                  item={item.presentStep}
                  onClick={() => {
                    const { id, resourceVersions } = item.presentStep;
                    const isResource =
                      resourceVersions && resourceVersions.length > 0;
                    if (isResource) {
                      navigate(Routes.Course.Resource.replace(':id', id));
                    } else {
                      navigate(Routes.Course.Form.replace(':id', id));
                    }
                  }}
                />
              </Fragment>
            ))}
          </div>
          <LanguageButton className="ml-6 hidden xl:flex" />
          {isDesktopLarge && (
            <Button
              component={Link}
              color="secondary"
              to={Routes.Profile.Home}
              aria-label={t('manageAccount')}
              className={twMerge(commonButtonStyle, 'ml-8')}
            >
              <AccentMaskIcon className="h-6 w-6" src="/images/user.svg" />
            </Button>
          )}
          <BurgerButton
            isOpen={isMobileMenuOpen}
            className="md:ml-8 xl:hidden"
            onClick={handleMobileMenuToggle}
          />
        </div>
      </div>
      {isMobileMenuOpen && (
        <ResponsiveMenu
          links={links}
          pathname={pathname}
          isGuestUser={isGuestUser}
          onClick={handleLinkClick}
        />
      )}
    </>
  );
};
